<template>
    <div :class="[classes, {
            '-bottom': simple !== false && simple % 2 == 0,
        }]" :style="styles">
        
        <svg
            v-if="isMobileOrTablet || (simple === false) && displayText"
            xmlns="http://www.w3.org/2000/svg"
            :viewBox="view"
        >
            <path :d="circlePath" :id="`path-${idlink}`" />
            <SvgTextOffset v-bind="{ idlink, offset, text: displayText, story }" />
        </svg>
        <div 
            v-else-if="text"
            :class="['slide__circle-text']" 
        >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 40">
                <SvgTextInline v-bind="{ text }" />
            </svg>
        </div>

        <circle-video
            v-if="type == 'video'"
            :media="media"
            :custom="custom"
        />

        <template v-else>
            <circle-image
                ref="circle"
                :id="id"
                :media="media"
                :text="displayText"
                :single="single"
                :slug="slug"
                :alttext="alttext"
            />
            <circle-image
                v-if="displayText == 'award-winning culture'"
                :id="id"
                :media="media + '_26'"
                :text="displayText"
                :slug="'belong'"
                :alttext="alttext"
            />
        </template>

        <circle-button
            v-if="story"
            ref="circleButton"
            text="See more"
            :author="media"
            :id="story"
            :slug="displayText"
        />

        <slot />

    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import buttonPosition from '@/mixins/lib/buttonPosition'
import CircleButton from './CircleButton'
import CircleImage  from './CircleImage'
import CircleVideo from './CircleVideo'

const BUBBLE_DIMENSIONS = {
    cx: 50,
    cy: 50,
    r: 50
}

const BUBBLE_UPSIDE_DOWN_TEXTS = [
    'leads', 'holistic', 'commitment', 'unique'
]

const alignProps = {
    type: {
        type: String,
        default: 'image'
    },
    offset: {
        type: [Number, String],
        default: 0
    },
    degree: {
        type: [Number, String],
        default: 0
    },
}

const dataProps = {
    text    : [String, Array],
    slug    : String,
    alttext : {
        type: String,
        default: ''
    },
    media: {
        default: ''
    },
    story: {
        default: null
    }
}

const styleProps = {
    custom  : Boolean,
    single  : Boolean,
    styled: {
        type: Boolean,
        default: false
    },
    simple: {
        type: [Boolean, Number],
        default: false
    },
}

const SvgTextInline = {
    props: ['text'],
    computed: {
        textLines() {
            if (this.text instanceof Array)
                return this.text 
                return [this.text]
        },
        startX() {
            if (this.textLines.length > 1) 
                return 26
                return 52
        },
    },
    render(h) {
        const { textLines, startX } = this
        const textSpans = textLines.map((line, x) => h('tspan', {
                attrs: {
                    'text-anchor': 'middle',
                    'alignment-baseline': 'middle',
                    x: '50%', 
                    y: `${( startX * .1 + 1.65* (x+1) *startX )}%`,
                },
            }, line)
        )
        return h('text', textSpans)
    }
}

function hasUpsideDownText({ text, story }) {
    if ( story instanceof Object && story.id > 1 ) {
        return false
    }

    const firstWordFromText = text 
        ? text.split(' ')[0] 
        : ''
    
    return BUBBLE_UPSIDE_DOWN_TEXTS
        .some(el => el.startsWith(firstWordFromText))
}

const SvgTextOffset = {
    functional: true,
    props: ['story', 'text', 'offset', 'idlink'],
    render(h, a) {
        const isUpsideDown = hasUpsideDownText(a.props) && !a.parent.isMobile()

        return h('text', {
            attrs: { 
                dy: isUpsideDown ? '2.5px' : undefined,
            }
        }, [
            h('textPath', {
                attrs: {
                    'xlink:href': `#path-${a.props.idlink}`,
                    class: isUpsideDown ? 'upside' : '',
                    startOffset: a.props.offset
                },
                domProps: {
                    innerHTML: a.props.text
                },
            })
        ])
    }
}

export default {
    name: 'CircleBox',
    components: {
        SvgTextOffset,
        SvgTextInline,
        CircleButton,
        CircleVideo, //: () => import('./CircleVideo'),
        CircleImage //: () => import('./CircleImage')
    },
    props: {
        ...alignProps,
        ...dataProps,
        ...styleProps,
    },
    computed: {
        id() {
            return [...this.$vnode.key].pop()
        },
        idlink() {
            return Math.floor(Math.random() * 500)
        },
        circlePath() {
            const { cx, cy, r } = BUBBLE_DIMENSIONS
            const text = { 
                text: this.displayText, 
                story: this.story
            }

            let sweep = 1; if ( hasUpsideDownText(text) && !this.isMobile() ) {
                sweep = 0
            }
		
            return `
                M ${cx - r} ${cy}
                a ${r},${r} 0 1,${sweep} ${r * 2},0
                a ${r},${r} 0 1,${sweep} -${r * 2},0
            `
        },
        isMobileOrTablet() {
            return this.isBrowserMobile || this.isBrowserPortrait
        },
        displayText() {
            const { text } = this
            if (text instanceof Array) 
                return text.join(' ')
                return text
        },
        classes() {
            return `circle circle--${this.type}`
        },
        styles() {
            if ( this.styled )
                return `height: ${this.size}px;`
                return null
        },
        ...mapGetters({
            browserSize: 'browser/size',
            isBrowserMobile: 'browser/isMobile',
            isBrowserPortrait: 'browser/isTabletPortrait',
        })
    },
    data() {
        return {
            size: 200,
            width: BUBBLE_DIMENSIONS.r * 2,
            emitted: false,
            view: `-5 -5 ${ BUBBLE_DIMENSIONS.cx * 2 + 10 } ${ BUBBLE_DIMENSIONS.cy * 2 + 10 }`,
        }
    },
    activated() {
        this.handleButton()
    },
    mounted() {
        this.handleButton()
    },
    watch: {
        '$store.state.user.type'() {
            this.handleButton()
        },
        browserSize() {
            this.handleButton()
        },
    },
    methods: {
        handleButton() {
            this.circleSize()
            this.position( this.$refs.circleButton, this.degree )
        },
        circleSize() {
            this.size = this.$el.clientWidth
        },
        position( el, degrees ) {

            if ( this.story && el ) {

                const width = el.$el.clientWidth
                const space = width + .5 * (this.size - this.$refs.circle.$el.clientWidth)
                
                const point = buttonPosition(this.size, space, degrees)

                el.$el.style.height = `${width}px`
                el.$el.style.left   = `calc(${point[0]}px)`
                el.$el.style.top    = `calc(${point[1]}px)`

                if ( !this.emitted ) {
                    this.$emit('newPoint', {id: this.id, el: el.$el})
                    this.emitted = true
                }

            }

        }
    },
}
</script>
